
import { env } from '../../../env'
import { useSearchParams } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import axios from 'axios'
import { useEffect } from 'react'
import { notification } from '../../../components/Notifications'
import { notifyError } from '../../../utils/notifyError'
import { useMutation } from '@tanstack/react-query'
import { useLogout } from '../../../hooks/useLogout'
import useAsyncEffect from 'use-async-effect'

const LOGIN_REDIRECT_URI = `${window.location.origin}/login`

export const useEdlink = (): (() => void) => {
  const { logOutUser } = useLogout()
  const [searchParams] = useSearchParams()
  const [cookies, setCookie, removeCookie, updateCookies] = useCookies(['edlink_access_token_exists', 'edlink_refresh_token_exists'])
  const stateKeyEdlink = searchParams.get('state')
  const codeKeyFromEdlink = searchParams.get('code')
  const errorKeyFromEdlink = searchParams.get('error')

  const isEdlinkLoginTokenValid = cookies.edlink_access_token_exists || cookies.edlink_refresh_token_exists

  const redirectToEdlinkLogin = (): void => {
    const state = window.crypto.randomUUID()
    localStorage.setItem('edlinkState', state)
    const EDLINK_SSO_URL = `https://ed.link/sso/login?client_id=${env.REACT_APP_EDLINK_CLIENT_ID}&redirect_uri=${encodeURI(LOGIN_REDIRECT_URI)}&response_type=code&state=${state}`
    window.location.href = EDLINK_SSO_URL
  }

  // • Centralized error UX for any Edlink failure (callback error or token exchange failure)
  const showErrorForEdlinkFailure = (): void => {
    notification(
      'Edlink SSO failed. Please try logging in again.',
      { variant: 'error' }
    )
  }
  // • Exchanges Edlink OAuth `code` for app tokens via backend (`/api/auth/token`)
  // • On success: set authType + infer tenantKey from email domain, refresh cookie state, then hard redirect to home
  // • On failure: show error and log out to clear any partial auth state
  const { mutate } = useMutation({
    mutationKey: ['edlinkSso'],
    mutationFn: async (edlinkCode: string) => {
      const response = await axios.post('/api/auth/token', {
        code: edlinkCode,
        redirect_uri: LOGIN_REDIRECT_URI
      })
      return response.data
    },
    onSuccess: (data: any) => {
      localStorage.setItem('authType', 'edlink')
      const urlSearch = data?.email?.split('@')?.[1]
      localStorage.setItem('tenantKey', urlSearch)
      updateCookies()
      window.location.href = '/'
    },
    onError: (error: unknown) => {
      // Surface the backend's error message (e.g. the Edlink-timeout guidance to
      // try manual login / forgot password); fall back to the generic copy.
      // `messageKey: 'error'` reads the backend envelope's `{ error }` field directly
      // (raw axios call, not the interceptor) instead of the generic axios status text.
      notifyError(error, { messageKey: 'error', fallbackMessage: 'Edlink SSO failed. Please try logging in again.' })
      void logOutUser()
    }
  })

  // • Callback handler:
  //   - If `code` exists: immediately exchange it for tokens (SSO completes)
  //   - Else if tokens already exist: skip login and navigate home
  // • Note: dependencies should include `mutate`, `navigate`, and `isEdlinkLoginTokenValid` for correctness
  useAsyncEffect(async () => {
    if (codeKeyFromEdlink) {
      // try {
      //   // Only logout if user is currently authenticated (Edlink or Cognito)
      //   if (isAuthed) {
      //     await logOutUser()
      //     if (hasUserPoolConfigured()) {
      //       await signOut()
      //     }
      //   }
      // } catch (e) {
      //   console.log(e)
      // } finally {
      //   mutate(codeKeyFromEdlink)
      // }

      mutate(codeKeyFromEdlink)
    }
    // @TODO: Review if this is needed anymore
    // if (isEdlinkLoginTokenValid) {
    //   console.log('hittt')
    //   // navigate('/', { replace: true, state: { fromLogin: true } })
    // }
  }, [codeKeyFromEdlink, stateKeyEdlink])

  // • If Edlink returns an OAuth error param, treat it as a failed login and reset auth state
  useEffect(() => {
    if (errorKeyFromEdlink) {
      showErrorForEdlinkFailure()
      void logOutUser()
    }
  }, [errorKeyFromEdlink])

  return redirectToEdlinkLogin
}
